/*!
 * (c) 2026 Ocean Essentials Limited (HK). All rights reserved.
 * Exocet Home Hero Motion — GSAP entrance and scroll presence
 */
(function () {
  "use strict";

  var BOUND = "data-home-hero-motion-bound";

  function pluginsReady() {
    return typeof gsap !== "undefined" && typeof ScrollTrigger !== "undefined";
  }

  function reducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  function panelCentered() {
    return window.matchMedia("(min-width: 750px)").matches;
  }

  function panelMotion(panel, values) {
    if (panel && panelCentered()) {
      // The panel is centered in CSS via transform: translateX(-50%). GSAP reads
      // that as pixels, so we must zero x and let xPercent own the centering --
      // otherwise the two stack and the panel drifts left by half its width.
      values.xPercent = -50;
      values.x = 0;
    }
    return values;
  }

  function ready(fn) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn, { once: true });
    } else {
      fn();
    }
  }

  function waitForHeroMedia(hero, done) {
    var video = hero.querySelector(".home-hero__video");
    var completed = false;

    function complete() {
      if (completed) return;
      completed = true;
      done();
    }

    if (!video || video.tagName !== "VIDEO" || video.readyState >= 2) {
      complete();
      return;
    }

    video.addEventListener("loadeddata", complete, { once: true });
    video.addEventListener("canplay", complete, { once: true });
    window.setTimeout(complete, window.matchMedia("(max-width: 749px)").matches ? 3000 : 1800);
  }

  function getHeroFrameStyles(hero) {
    var styles = getComputedStyle(hero);
    return {
      width: (styles.getPropertyValue("--hero-frame-w") || "min(758px, 88vw)").trim(),
      height: (styles.getPropertyValue("--hero-frame-h") || "clamp(280px, 42vh, 322px)").trim(),
      radius: (styles.getPropertyValue("--hero-frame-radius") || "16px").trim(),
    };
  }

  function setHeroFrameMedia(media, frame) {
    gsap.set(media, {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      width: frame.width,
      height: frame.height,
      borderRadius: frame.radius,
      x: 0,
      y: 0,
      xPercent: -50,
      yPercent: -50,
      overflow: "hidden",
    });
  }

  function finishHero(hero) {
    hero.classList.add("home-hero--entrance-done");
    hero.classList.remove(
      "home-hero--entering",
      "home-hero--media-fade",
      "home-hero--media-expand",
      "home-hero--header-in",
      "home-hero--overlay-in",
      "home-hero--content-in",
    );
    document.dispatchEvent(new CustomEvent("home-hero:entrance-complete", { detail: { heroId: hero.id } }));
    initMetaTypewriter(hero);
  }

  function isMobile() {
    return window.matchMedia("(max-width: 749px)").matches;
  }

  function parseTypewriterPhrases(el) {
    if (!el) return [];
    try {
      return JSON.parse(el.getAttribute("data-phrases") || "[]")
        .map(function (phrase) {
          return String(phrase || "").trim();
        })
        .filter(Boolean);
    } catch (error) {
      return [];
    }
  }

  function typewriterTo(el, value, duration, onComplete) {
    var state = { progress: 0 };
    el.textContent = "";
    gsap.to(state, {
      progress: value.length,
      duration: duration,
      ease: "none",
      onUpdate: function () {
        el.textContent = value.slice(0, Math.round(state.progress));
      },
      onComplete: onComplete,
    });
  }

  function initMetaTypewriter(hero) {
    if (hero.hasAttribute("data-hero-typewriter-bound")) return;

    var el = hero.querySelector(".text[data-hero-typewriter]");
    if (!el) return;

    hero.setAttribute("data-hero-typewriter-bound", "");

    var phrases = parseTypewriterPhrases(el);
    if (!phrases.length) return;

    if (reducedMotion() || !pluginsReady()) {
      el.textContent = phrases[0];
      return;
    }

    var index = 0;

    function playNext() {
      if (!el.isConnected) return;
      typewriterTo(el, phrases[index], 1.4, function () {
        if (phrases.length === 1) return;
        gsap.delayedCall(2, function () {
          index = (index + 1) % phrases.length;
          playNext();
        });
      });
    }

    playNext();
  }

  function initEntrance(hero, onDone) {
    var media = hero.querySelector(".home-hero__media");
    var overlay = hero.querySelector(".home-hero__overlay");
    var panel = hero.querySelector(".home-hero__panel");
    var revealItems = hero.querySelectorAll("[data-hero-reveal]");
    var header = document.querySelector("[data-header-wrapper]");

    document.documentElement.classList.add("home-hero-motion-ready");

    if (!hero.classList.contains("home-hero--entering")) {
      gsap.set([media, overlay, panel, revealItems], { clearProps: "all" });
      initMetaTypewriter(hero);
      if (typeof onDone === "function") onDone();
      return;
    }

    if (isMobile()) {
      hero.classList.add("home-hero--header-in");

      var video = hero.querySelector(".home-hero__video");
      var frame = getHeroFrameStyles(hero);

      setHeroFrameMedia(media, frame);
      gsap.set(media, { autoAlpha: 1 });
      if (video) gsap.set(video, { autoAlpha: 0 });
      gsap.set(overlay, { autoAlpha: 0 });
      gsap.set(panel, panelMotion(panel, { autoAlpha: 0, y: 24 }));
      gsap.set(revealItems, { autoAlpha: 0, y: 12 });

      waitForHeroMedia(hero, function () {
        var expandDuration = 0.9;
        var pauseDuration = 0.2;
        var expandStart = 0.15;
        var fullscreenAt = expandStart + expandDuration;
        var afterPause = fullscreenAt + pauseDuration;

        var mobileTl = gsap.timeline({
          defaults: { overwrite: "auto" },
          onComplete: function () {
            gsap.set([media, overlay, panel, revealItems, video], { clearProps: "all" });
            finishHero(hero);
            if (typeof onDone === "function") onDone();
          },
        });

        if (video) {
          mobileTl.to(video, { autoAlpha: 1, duration: 0.25, ease: "power2.out" }, 0);
        }
        mobileTl.to(
          media,
          {
            width: "100%",
            height: "100%",
            borderRadius: 0,
            duration: expandDuration,
            ease: "expo.inOut",
          },
          expandStart,
        );
        mobileTl.to(overlay, { autoAlpha: 1, duration: 0.55, ease: "sine.inOut" }, afterPause);
        mobileTl.to(
          panel,
          panelMotion(panel, { autoAlpha: 1, y: 0, duration: 0.7, ease: "sine.out" }),
          afterPause + 0.12,
        );
        mobileTl.to(
          revealItems,
          { autoAlpha: 1, y: 0, duration: 0.55, stagger: 0.1, ease: "sine.out" },
          afterPause + 0.28,
        );
      });
      return;
    }

    var frame = getHeroFrameStyles(hero);

    setHeroFrameMedia(media, frame);
    gsap.set(media, { autoAlpha: 0 });
    gsap.set(overlay, { autoAlpha: 0 });
    gsap.set(panel, panelMotion(panel, { autoAlpha: 0, y: 32 }));
    gsap.set(revealItems, { autoAlpha: 0, y: 14 });
    if (header) gsap.set(header, { autoAlpha: 0, y: -12 });

    waitForHeroMedia(hero, function () {
      var tl = gsap.timeline({
        defaults: { overwrite: "auto" },
        onComplete: function () {
          gsap.set([media, overlay, panel, revealItems, header], { clearProps: "all" });
          finishHero(hero);
          if (typeof onDone === "function") onDone();
        },
      });

      tl.to(media, { autoAlpha: 1, duration: 0.55, ease: "power2.inOut" }, 0);
      // Keep the frame anchored at center (top/left 50% + translate -50%) and only
      // grow width/height. Animating top/left (parent-relative %) alongside
      // xPercent/yPercent (element-relative %) while the size changes drifts the
      // center toward the top-left mid-tween -- it reads as "opening from the
      // corner" instead of expanding symmetrically from the middle.
      var expandStart = 0.5;
      var expandDuration = 1.45;
      var fullscreenAt = expandStart + expandDuration;

      tl.to(
        media,
        {
          width: "100%",
          height: "100%",
          borderRadius: 0,
          duration: expandDuration,
          ease: "expo.inOut",
        },
        expandStart,
      );

      // Fullscreen first, then overlay → panel → text cascade → header last.
      tl.to(overlay, { autoAlpha: 1, duration: 0.75, ease: "sine.inOut" }, fullscreenAt);
      tl.to(panel, panelMotion(panel, { autoAlpha: 1, y: 0, duration: 0.85, ease: "sine.out" }), fullscreenAt + 0.15);
      tl.to(revealItems, { autoAlpha: 1, y: 0, duration: 0.65, stagger: 0.1, ease: "sine.out" }, fullscreenAt + 0.42);

      if (header) {
        var headerAt = fullscreenAt + 0.42 + 0.65 + Math.max(0, revealItems.length - 1) * 0.1 + 0.18;
        tl.call(
          function () {
            hero.classList.add("home-hero--header-in");
          },
          null,
          headerAt,
        );
        tl.to(header, { autoAlpha: 1, y: 0, duration: 0.72, ease: "sine.out" }, headerAt);
      }
    });
  }

  function initScroll(hero) {
    var media = hero.querySelector(".home-hero__media");
    var panel = hero.querySelector(".home-hero__panel");
    if (!media && !panel) return;

    gsap.fromTo(
      media,
      { yPercent: 0, scale: 1 },
      {
        yPercent: 8,
        scale: 1.035,
        ease: "none",
        scrollTrigger: {
          trigger: hero,
          start: "top top",
          end: "bottom top",
          scrub: 0.65,
        },
      },
    );

    if (panel) {
      var panelScroll = isMobile() ? { yPercent: -4, ease: "none" } : { yPercent: -8, autoAlpha: 0.72, ease: "none" };

      gsap.to(
        panel,
        panelMotion(panel, {
          ...panelScroll,
          scrollTrigger: {
            trigger: hero,
            start: "35% top",
            end: "bottom top",
            scrub: 0.5,
          },
        }),
      );
    }
  }

  function boot(root, designMode) {
    var scope = root || document;
    var gsapReady = pluginsReady();

    if (gsapReady) {
      gsap.registerPlugin(ScrollTrigger);
    }

    scope.querySelectorAll(".home-hero").forEach(function (hero) {
      if (hero.hasAttribute(BOUND)) return;
      hero.setAttribute(BOUND, "");

      if (reducedMotion() || designMode) {
        finishHero(hero);
        return;
      }

      if (!gsapReady) {
        finishHero(hero);
        return;
      }

      var safetyTimer = window.setTimeout(function () {
        if (hero.classList.contains("home-hero--entering") && !hero.classList.contains("home-hero--entrance-done")) {
          finishHero(hero);
        }
      }, 6500);

      initEntrance(hero, function () {
        window.clearTimeout(safetyTimer);
        initScroll(hero);
      });
    });

    if (gsapReady) {
      ScrollTrigger.refresh();
    }
  }

  function bindScrollCue(hero) {
    var btn = hero.querySelector("[data-hero-scroll-cue]");
    if (!btn) return;
    btn.addEventListener("click", function () {
      var next = hero.nextElementSibling;
      if (!next) return;
      var prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      next.scrollIntoView({ behavior: prefersReduced ? "auto" : "smooth", block: "start" });
    });
  }

  ready(function () {
    document.querySelectorAll(".home-hero").forEach(function (hero) {
      bindScrollCue(hero);
    });
    boot(document, false);
  });

  if (typeof Shopify !== "undefined" && Shopify.designMode) {
    document.addEventListener("shopify:section:load", function (event) {
      event.target.querySelectorAll(".home-hero").forEach(function (hero) {
        bindScrollCue(hero);
      });
      boot(event.target, false);
    });

    document.addEventListener("shopify:section:unload", function (event) {
      if (!pluginsReady()) return;
      ScrollTrigger.getAll().forEach(function (st) {
        if (st.trigger && event.target.contains(st.trigger)) st.kill();
      });
    });

    document.addEventListener("shopify:section:select", function (event) {
      boot(event.target, true);
    });
  }

  window.ExocetHomeHeroMotion = {
    refresh: function () {
      if (pluginsReady()) ScrollTrigger.refresh();
    },
    boot: boot,
  };

  if (window.ExocetMotion) {
    ExocetMotion.register(window.ExocetHomeHeroMotion);
  }
})();
